// B2COrdersPage.tsx
import React from "react";
import {
  Box, Text, VStack, HStack, Badge, Divider,
  useColorModeValue, Heading, Flex, Icon,
} from "@chakra-ui/react";
import { FaShoppingBag, FaCheckCircle, FaClock, FaBoxOpen } from "react-icons/fa";
import { useStudentContext } from "../B2CStudentContext";
import { fmtLocal } from "../../../utils/dates";
import { StudentEntitlement, StudentPurchase } from "../../../Platform/Common/types";

const B2COrdersPage = () => {
  const { studentContext } = useStudentContext();
  const cardBg = useColorModeValue("white", "gray.800");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const entitlementBg = useColorModeValue("gray.50", "gray.750");

  const purchases: StudentPurchase[] = studentContext?.purchases ?? [];
  const entitlements: StudentEntitlement[] = studentContext?.entitlements ?? [];

  // Group entitlements by purchase_id for display alongside each order
  const entitlementsByPurchase = entitlements.reduce<Record<number, StudentEntitlement[]>>(
    (acc, ent) => {
      if (!acc[ent.purchase_id]) acc[ent.purchase_id] = [];
      acc[ent.purchase_id].push(ent);
      return acc;
    },
    {}
  );

  return (
    <Box maxW="900px" mx="auto" p={6}>
      <VStack align="start" spacing={1} mb={8}>
        <Heading size="lg">Orders & Purchases</Heading>
        <Text color={mutedText}>Your complete purchase history and active entitlements</Text>
      </VStack>

      {purchases.length === 0 ? (
        <Box textAlign="center" py={16} color={mutedText}>
          <Icon as={FaShoppingBag} w={10} h={10} mb={4} />
          <Text fontSize="lg">No purchases yet.</Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={5}>
          {purchases.map((purchase) => {
            const linkedEntitlements = entitlementsByPurchase[purchase.purchase_id] ?? [];

            return (
              <Box
                key={purchase.purchase_id}
                bg={cardBg}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                p={5}
              >
                {/* Order header */}
                <Flex justify="space-between" align="start" mb={3}>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <Icon as={FaShoppingBag} color={mutedText} />
                      <Text fontWeight="bold" fontSize="md">
                        Order #{purchase.purchase_reference}
                      </Text>
                    </HStack>
                    {purchase.completed_at && (
                      <Text fontSize="sm" color={mutedText}>
                        Placed on{" "}
                        {fmtLocal(purchase.completed_at, {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Text>
                    )}
                  </VStack>

                  <VStack align="end" spacing={1}>
                    <Text fontWeight="bold" fontSize="lg">
                      {purchase.currency}{" "}
                      {purchase.total_amount}
                    </Text>
                  </VStack>
                </Flex>

                <Divider mb={3} />

                {/* Entitlements under this purchase */}
                {linkedEntitlements.length === 0 ? (
                  <Text fontSize="sm" color={mutedText}>No entitlements linked to this order.</Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {linkedEntitlements.map((ent) => {
                      const now = new Date();
                      const endDate = ent.end_date ? new Date(ent.end_date) : null;
                      const isActive = ent.status === "ACTIVE" && (!endDate || endDate > now);
                      const practiceSet = ent.practice_set;

                      return (
                        <Box
                          key={ent.entitlement_id}
                          bg={entitlementBg}
                          borderRadius="lg"
                          p={4}
                          borderWidth="1px"
                          borderColor={isActive ? "green.100" : "gray.200"}
                        >
                          <Flex justify="space-between" align="start">
                            <HStack align="start" spacing={3}>
                              <Icon
                                as={isActive ? FaCheckCircle : FaClock}
                                color={isActive ? "green.400" : "gray.400"}
                                mt={1}
                                flexShrink={0}
                              />
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="semibold" fontSize="sm">
                                  {practiceSet?.title ?? `Content ID: ${ent.content_id}`}
                                </Text>

                                {practiceSet?.edition_label && (
                                  <Badge colorScheme="purple" fontSize="2xs">
                                    {practiceSet.edition_label}
                                  </Badge>
                                )}

                                {(practiceSet?.state || practiceSet?.description) && (
                                  <Text fontSize="xs" color={mutedText}>
                                    {[practiceSet.state, practiceSet.description]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </Text>
                                )}

                                <HStack spacing={4} fontSize="xs" color={mutedText} pt={1}>
                                  {ent.start_date && (
                                    <Text>
                                      From:{" "}
                                      <Text as="span" fontWeight="medium">
                                        {fmtLocal(ent.start_date, {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </Text>
                                    </Text>
                                  )}
                                  {ent.end_date && (
                                    <Text>
                                      {isActive ? "Expires" : "Expired"}:{" "}
                                      <Text
                                        as="span"
                                        fontWeight="medium"
                                        color={isActive ? "inherit" : "red.400"}
                                      >
                                        {fmtLocal(ent.end_date, {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </Text>
                                    </Text>
                                  )}
                                </HStack>
                              </VStack>
                            </HStack>

                            <Badge
                              colorScheme={isActive ? "green" : "gray"}
                              fontSize="xs"
                              px={2}
                              py={1}
                              borderRadius="full"
                              flexShrink={0}
                            >
                              {isActive ? "Active" : ent.status}
                            </Badge>
                          </Flex>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default B2COrdersPage;